const PRODUCT_CATEGORIES = [
  {
    title: "Utility Applications",
    subtitle: "Cleaners, Optimizers & System Tools",
    desc: "High-performance mobile and desktop utilities designed to enhance device performance, clean up storage, manage files, and optimize system resources — all with intuitive, user-friendly interfaces.",
    icon: "ri-tools-line",
    highlights: ["System cleaners", "Storage optimizers", "File managers", "Battery savers"],
    image: "https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20a%20clean%20utility%20app%20interface%20with%20storage%20cleaner%20and%20optimizer%20tools%2C%20sleek%20device%20on%20warm%20minimal%20cream%20background%2C%20professional%20product%20photography%2C%20soft%20natural%20lighting%2C%20clean%20composition%2C%20warm%20amber%20and%20neutral%20tones%2C%20high%20detail%2C%20sophisticated%20tech%20aesthetic%2C%20no%20text%20on%20screen&width=900&height=675&seq=product-utility-v2&orientation=landscape",
  },
  {
    title: "AI-Powered Services",
    subtitle: "Smart Tools & Automation",
    desc: "We integrate artificial intelligence into utility applications — from smart photo organizers and AI-based duplicate finders to intelligent notification managers and predictive text tools.",
    icon: "ri-robot-2-line",
    highlights: ["AI photo organizers", "Smart duplicate finders", "Predictive assistants", "Automation scripts"],
    image: "https://storage.readdy-site.link/project_files/564b8566-d4d5-4635-bf3c-ab9b4b833508/4f8b90a2-083e-494a-adc8-419cb07b3bed_compressed_Screenshot-2026-07-13-at-15.14.42.webp",
  },
  {
    title: "Performance Tools",
    subtitle: "Speed & Optimization",
    desc: "Dedicated tools for monitoring and improving device performance — CPU and memory optimizers, network speed boosters, and real-time system health dashboards for power users.",
    icon: "ri-speed-up-line",
    highlights: ["CPU & RAM optimizers", "Network boosters", "System monitors", "Gaming mode tools"],
    image: "https://readdy.ai/api/search-image?query=High-performance%20digital%20speedometer%20and%20system%20optimization%20dashboard%20on%20modern%20dark%20interface%2C%20dynamic%20speed%20lines%20and%20performance%20metrics%2C%20warm%20amber%20and%20gold%20glowing%20accents%2C%20professional%20tech%20aesthetic%2C%20clean%20sophisticated%20design%2C%20high%20detail%2C%20corporate%20technology%20visualization%2C%20no%20text&width=900&height=675&seq=product-perf-v3&orientation=landscape",
  },
];

export default function Products() {
  return (
    <section id="products" className="relative w-full bg-cream-50 py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        {/* Heading */}
        <div className="flex flex-col items-center text-center mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
              Our Products
            </span>
          </div>
          <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl max-w-3xl">
            Utility apps built for
            <br />
            real-world performance
          </h2>
          <p className="mt-5 max-w-xl text-sm md:text-base text-ink-700 leading-relaxed">
            Each product is developed with a focus on speed, reliability, and user experience.
            We build tools people actually want to use every day.
          </p>
        </div>

        {/* Product cards */}
        <div className="space-y-8 md:space-y-10">
          {PRODUCT_CATEGORIES.map((cat, i) => {
            const isReversed = i % 2 === 1;
            return (
              <div
                key={cat.title}
                className={`grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 items-center`}
              >
                {/* Image */}
                <div className={`lg:col-span-2 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative rounded-2xl overflow-hidden border border-ink-950/10">
                    <div className="relative aspect-[4/3] w-full">
                      <img
                        src={cat.image}
                        alt={cat.title}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </div>

                {/* Text */}
                <div className={`lg:col-span-3 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-moss-100">
                      <i className={`${cat.icon} text-xl text-moss-600`}></i>
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-2xl md:text-3xl text-ink-950">
                        {cat.title}
                      </h3>
                      <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mt-0.5">
                        {cat.subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-ink-700 leading-relaxed mt-4">
                    {cat.desc}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {cat.highlights.map((h) => (
                      <span
                        key={h}
                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-cream-100 border border-ink-950/5 text-xs font-medium text-ink-700"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-sm text-ink-500 mb-4">Interested in our products or want to build one together?</p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-ink-950 hover:bg-ink-900 text-cream-50 px-7 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            Discuss your project
            <i className="ri-arrow-right-line text-base"></i>
          </a>
        </div>
      </div>
    </section>
  );
}